import { supabase } from '../lib/supabaseClient';
import { QRCodeData, QRCodeCustomization } from '../types';

export interface StaticQRInput {
  title: string;
  content: string;
  type: QRCodeData['type'];
  customization: QRCodeCustomization;
  qr_image?: string; // Base64 or URL to the QR code image
}

export interface StaticQRRow {
  id: number;
  user_id: string;
  title: string;
  content: string;
  type: string;
  customization: QRCodeCustomization;
  qr_image?: string;
  created_at: string;
  updated_at: string;
}

export async function saveStaticQR(input: StaticQRInput): Promise<StaticQRRow> {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated. Please sign in to save QR codes.');
  }

  const { data, error } = await supabase
    .from('static_qr')
    .insert([
      {
        user_id: userData.user.id,
        title: input.title,
        content: input.content,
        type: input.type,
        customization: input.customization,
        qr_image: input.qr_image || null,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data as StaticQRRow;
}

export async function listMyStaticQR(limit = 50, offset = 0): Promise<StaticQRRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('static_qr')
    .select('*')
    .eq('user_id', userData.user.id)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data as StaticQRRow[];
}

export async function deleteStaticQR(id: number): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { error } = await supabase
    .from('static_qr')
    .delete()
    .eq('id', id)
    .eq('user_id', userData.user.id); // Ensure user can only delete their own QR codes

  if (error) throw error;
}

export async function updateStaticQR(
  id: number,
  updates: Partial<{
    title: string;
    content: string;
    type: string;
    customization: QRCodeCustomization;
    qr_image: string;
  }>
): Promise<StaticQRRow> {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('static_qr')
    .update(updates)
    .eq('id', id)
    .eq('user_id', userData.user.id)
    .select()
    .single();

  if (error) throw error;
  return data as StaticQRRow;
}
